import React, { useState, useCallback, useMemo, useRef, useEffect } from "react";
import ReactCrop, { centerCrop, makeAspectCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { useTranslation } from "react-i18next";
import { RotateCw, Check, X, Undo2, Move, Crop as CropIcon, Frame } from "lucide-react";

/**
 * Modal photo editor: rotate 90° + freeform / aspect-locked crop.
 *
 * Uses `react-image-crop`, which renders a resizable rectangular crop box
 * with corner + edge handles the user can drag freely. When `aspect` is
 * undefined the box can be any shape; when `aspect` is a number, the box
 * stays that ratio while still resizable. (Previously we used
 * `react-easy-crop`, which is a pan-and-zoom cropper with no draggable
 * crop-box — the "Free" option was broken because there was nothing to
 * resize.)
 *
 * Rotation is applied by pre-baking the rotated image into a canvas and
 * feeding that as the cropper's source, so we never have to compensate for
 * rotation math in the crop step itself.
 *
 * `onConfirm(dataUrl)` returns a JPEG data URL of the cropped region.
 * "Done" is safe on every code path: if the user hasn't touched anything,
 * we pass the original image through unchanged.
 */

// Helper: rotate a source data URL by a 90° multiple and return the rotated
// data URL. No-op when rotation === 0. Uses a canvas so the pixel dimensions
// are updated (a 90° rotation of a 1000×1500 image → 1500×1000).
async function rotateDataUrl(sourceUrl, rotation) {
  if (!rotation) return sourceUrl;
  const norm = ((rotation % 360) + 360) % 360;
  if (norm === 0) return sourceUrl;
  const img = await new Promise((resolve, reject) => {
    const i = new Image();
    i.onload = () => resolve(i);
    i.onerror = reject;
    i.src = sourceUrl;
  });
  const radians = (norm * Math.PI) / 180;
  const w = img.width;
  const h = img.height;
  const outW = norm === 180 ? w : h;
  const outH = norm === 180 ? h : w;
  const c = document.createElement("canvas");
  c.width = outW;
  c.height = outH;
  const ctx = c.getContext("2d");
  ctx.translate(outW / 2, outH / 2);
  ctx.rotate(radians);
  ctx.drawImage(img, -w / 2, -h / 2);
  return c.toDataURL("image/jpeg", 0.95);
}

// Helper: apply a pixel-space crop to the currently displayed image and
// return a fresh JPEG data URL.
async function cropDataUrl(sourceUrl, cropPx) {
  const img = await new Promise((resolve, reject) => {
    const i = new Image();
    i.onload = () => resolve(i);
    i.onerror = reject;
    i.src = sourceUrl;
  });
  const c = document.createElement("canvas");
  c.width = Math.max(1, Math.round(cropPx.width));
  c.height = Math.max(1, Math.round(cropPx.height));
  c.getContext("2d").drawImage(
    img,
    Math.round(cropPx.x), Math.round(cropPx.y),
    Math.round(cropPx.width), Math.round(cropPx.height),
    0, 0, c.width, c.height,
  );
  return c.toDataURL("image/jpeg", 0.92);
}

export default function PhotoEditor({ open, image, onCancel, onConfirm }) {
  const { t } = useTranslation();
  // `workingImage` is the source we hand to ReactCrop. It's the original
  // image, re-baked whenever the user rotates so ReactCrop always sees an
  // upright rectangle.
  const [workingImage, setWorkingImage] = useState(image);
  const [rotation, setRotation] = useState(0);
  const [aspectKey, setAspectKey] = useState("free");
  // ReactCrop's `crop` prop can be in either % or px; we let the library
  // manage percent internally and only ask for `PixelCrop` on complete so
  // we can drive the final canvas from real pixel coordinates.
  const [crop, setCrop] = useState(null);
  const [completedCrop, setCompletedCrop] = useState(null);
  const [busy, setBusy] = useState(false);
  const imgRef = useRef(null);
  // A11y: focus management for the modal dialog.
  //  - `dialogRef` scopes focus-trap tab-cycling.
  //  - `cancelBtnRef` receives initial focus on mount.
  //  - `returnFocusRef` snapshots the element that opened the editor so we
  //    can restore focus to it when the dialog closes.
  const dialogRef = useRef(null);
  const cancelBtnRef = useRef(null);
  const returnFocusRef = useRef(null);

  // Rebuild workingImage whenever the parent hands us a fresh source (e.g.
  // opening the editor a second time on a different photo).
  useEffect(() => {
    setWorkingImage(image);
    setRotation(0);
    setAspectKey("free");
    setCrop(null);
    setCompletedCrop(null);
  }, [image]);

  // A11y: on mount, snapshot the previously-focused element and move focus
  // into the dialog (Cancel button). Trap Tab inside the dialog and treat
  // Escape as Cancel. On unmount restore focus to the invoking element so
  // keyboard users don't get dropped back at `<body>`.
  useEffect(() => {
    returnFocusRef.current = typeof document !== "undefined" ? document.activeElement : null;
    // Defer to next tick so React has painted the button.
    const focusTimer = setTimeout(() => {
      cancelBtnRef.current?.focus();
    }, 0);
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onCancel();
        return;
      }
      if (e.key !== "Tab" || !dialogRef.current) return;
      const focusable = dialogRef.current.querySelectorAll(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      clearTimeout(focusTimer);
      document.removeEventListener("keydown", handleKeyDown);
      // Restore focus to the invoking element on close.
      const returnTarget = returnFocusRef.current;
      if (returnTarget && typeof returnTarget.focus === "function") {
        try { returnTarget.focus(); } catch { /* ignore */ }
      }
    };
  }, []);

  const ASPECTS = useMemo(() => ([
    { key: "free",      ratio: null,   labelKey: "editor.aspectFree" },
    { key: "square",    ratio: 1,      labelKey: "editor.aspectSquare" },
    { key: "fourThree", ratio: 4 / 3,  labelKey: "editor.aspect43" },
    { key: "threeFour", ratio: 3 / 4,  labelKey: "editor.aspect34" },
    { key: "portrait",  ratio: 9 / 16, labelKey: "editor.aspectPortrait" },
  ]), []);
  const aspectRatio = (ASPECTS.find((a) => a.key === aspectKey) || ASPECTS[0]).ratio;

  // Convert a percent-space crop to display-pixel space so we can seed
  // `completedCrop` alongside `crop`. Without this sync, picking an
  // aspect and immediately tapping Apply would pass the original image
  // through (isDirty=false), because `completedCrop` only fires on a
  // real drag-end from ReactCrop.
  const percentToPixelCrop = useCallback((imgEl, pc) => {
    if (!imgEl || !pc) return null;
    const w = imgEl.width;
    const h = imgEl.height;
    return {
      unit: "px",
      x: (pc.x || 0) * w / 100,
      y: (pc.y || 0) * h / 100,
      width: (pc.width || 0) * w / 100,
      height: (pc.height || 0) * h / 100,
    };
  }, []);

  // Seed a sensible default crop box the first time the image loads OR
  // whenever the aspect changes — otherwise the user has to draw from
  // scratch every time.
  //
  // Aspect-locked seed picks its constraint (width vs height) by comparing
  // the target aspect to the image's own aspect. Without this, requesting
  // a 1:1 crop on a landscape photo produces a 90%-wide, 100%-tall crop
  // whose HEIGHT was silently clamped — the visible box ends up as a
  // rectangle, not a square, which is exactly the "Square doesn't work"
  // report from mobile Android. Choosing the shorter axis to be 90% of
  // the display size guarantees the crop actually fits AND stays the
  // requested ratio.
  //
  // Free seed uses ~92% instead of 100% so the corner handles have real
  // interior room to be grabbed from. A 100% seed pins the handles to
  // the image edges, which on Android competes with the browser's
  // edge-swipe / scroll gesture and eats every drag before ReactCrop
  // ever sees it — the reported "dotted line but no interaction".
  const seedCropForAspect = useCallback((imgEl, ratio) => {
    if (!imgEl) return;
    const { width, height } = imgEl;
    if (!width || !height) return;
    let next;
    if (ratio) {
      const imgAspect = width / height;
      const seedBase =
        ratio > imgAspect
          ? { unit: "%", width: 90 }   // target wider than image → limit width
          : { unit: "%", height: 90 }; // target taller/squarer → limit height
      next = centerCrop(
        makeAspectCrop(seedBase, ratio, width, height),
        width,
        height,
      );
    } else {
      // Free: inset the seed so the handles are grabbable on touch devices.
      next = { unit: "%", x: 4, y: 4, width: 92, height: 92 };
    }
    setCrop(next);
    setCompletedCrop(percentToPixelCrop(imgEl, next));
  }, [percentToPixelCrop]);

  const onImageLoad = useCallback((e) => {
    imgRef.current = e.currentTarget;
    seedCropForAspect(e.currentTarget, aspectRatio);
  }, [aspectRatio, seedCropForAspect]);

  const handleAspectChange = (key) => {
    setAspectKey(key);
    const ratio = (ASPECTS.find((a) => a.key === key) || ASPECTS[0]).ratio;
    seedCropForAspect(imgRef.current, ratio);
  };

  const rotate = async () => {
    // Snap to 90° increments; pre-bake so ReactCrop stays orientation-free.
    const next = (rotation + 90) % 360;
    const rotated = await rotateDataUrl(image, next);
    setRotation(next);
    setWorkingImage(rotated);
    // New pixel dims → drop the current crop so onImageLoad re-seeds it.
    setCrop(null);
    setCompletedCrop(null);
  };

  const reset = async () => {
    setRotation(0);
    setAspectKey("free");
    setWorkingImage(image);
    setCrop(null);
    setCompletedCrop(null);
  };

  const isDirty = useMemo(() => {
    if (rotation !== 0) return true;
    if (!completedCrop || !imgRef.current) return false;
    const iw = imgRef.current.naturalWidth;
    const ih = imgRef.current.naturalHeight;
    const c = completedCrop;
    // Any meaningful trim from the edges counts as dirty. 1px slack
    // absorbs floating-point drift when the user grabs but doesn't drag.
    return c.x > 1 || c.y > 1 || c.width < iw - 1 || c.height < ih - 1;
  }, [rotation, completedCrop]);

  if (!open || !image) return null;

  const apply = async () => {
    // Never silently dismiss: if untouched, pass the original through.
    if (!isDirty) {
      onConfirm(image);
      return;
    }
    setBusy(true);
    try {
      // `completedCrop` is in display pixels; convert to natural-image px.
      let out;
      if (completedCrop && imgRef.current) {
        const el = imgRef.current;
        const scaleX = el.naturalWidth / el.width;
        const scaleY = el.naturalHeight / el.height;
        const pxCrop = {
          x: completedCrop.x * scaleX,
          y: completedCrop.y * scaleY,
          width: completedCrop.width * scaleX,
          height: completedCrop.height * scaleY,
        };
        out = await cropDataUrl(workingImage, pxCrop);
      } else {
        out = workingImage;
      }
      onConfirm(out);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div ref={dialogRef} className="fixed inset-0 z-50 bg-[#0F1410]/95 backdrop-blur" data-testid="photo-editor" role="dialog" aria-modal="true" aria-labelledby="photo-editor-title">
      <div className="absolute inset-0 flex flex-col">
        {/* Header — explicit Cancel pill + clear primary action */}
        <div className="flex items-center justify-between gap-3 px-4 pt-4">
          <button
            ref={cancelBtnRef}
            type="button"
            onClick={onCancel}
            data-testid="editor-cancel"
            aria-label={t("editor.cancel")}
            className="rounded-full bg-white/10 hover:bg-white/15 text-white px-4 py-2 text-sm flex items-center gap-1.5 transition"
          >
            <X className="w-4 h-4" strokeWidth={1.5} />
            {t("editor.cancel")}
          </button>
          <h2 id="photo-editor-title" className="text-white font-display text-lg sm:text-xl truncate" data-testid="editor-title">
            {t("editor.title")}
          </h2>
          <button
            type="button"
            onClick={apply}
            disabled={busy}
            data-testid="editor-confirm"
            className="rounded-full bg-leaf hover:bg-leaf-deep disabled:opacity-60 text-white px-5 py-2 text-sm font-medium flex items-center gap-1.5 shadow-botanical transition"
          >
            <Check className="w-4 h-4" strokeWidth={1.5} />
            {busy ? t("editor.applying") : (isDirty ? t("editor.applyEdits") : t("editor.useAsIs"))}
          </button>
        </div>

        {/* Plain-English hint line — answers "what do I do here?" */}
        <div className="px-4 mt-3" data-testid="editor-hint">
          <div className="flex items-center justify-center gap-4 text-white/75 text-[11px] sm:text-xs">
            <span className="inline-flex items-center gap-1.5">
              <CropIcon className="w-3.5 h-3.5" strokeWidth={1.5} />
              {t("editor.hintCorners")}
            </span>
            <span className="opacity-40">·</span>
            <span className="inline-flex items-center gap-1.5">
              <Move className="w-3.5 h-3.5" strokeWidth={1.5} />
              {t("editor.hintDragBox")}
            </span>
          </div>
        </div>

        {/* Stage — react-image-crop renders the image + resizable crop rect. */}
        <div className="relative flex-1 mt-3 mx-3 rounded-3xl overflow-hidden bg-black flex items-center justify-center" data-testid="editor-stage">
          <ReactCrop
            crop={crop || undefined}
            onChange={(_, percentCrop) => setCrop(percentCrop)}
            onComplete={(c) => setCompletedCrop(c)}
            aspect={aspectRatio || undefined}
            keepSelection
            className="max-h-full"
          >
            <img
              ref={imgRef}
              src={workingImage}
              alt=""
              onLoad={onImageLoad}
              className="max-h-[100%] max-w-full object-contain select-none"
              style={{ touchAction: "none" }}
              draggable={false}
            />
          </ReactCrop>
          {rotation !== 0 && (
            <div className="absolute top-3 left-1/2 -translate-x-1/2 rounded-full bg-black/55 text-white text-[11px] px-3 py-1 flex items-center gap-2 backdrop-blur" data-testid="editor-status" role="status" aria-live="polite">
              <RotateCw className="w-3 h-3" strokeWidth={1.5} aria-hidden="true" /> {rotation}°
            </div>
          )}
        </div>

        {/* Controls */}
        <div className="px-5 pb-5 pt-3 space-y-3">
          {/* Aspect-ratio chips */}
          <div className="flex items-center gap-2 overflow-x-auto pb-1" data-testid="editor-aspects" role="group" aria-label={t("editor.aspectGroupAria")}>
            {ASPECTS.map((a) => {
              const active = a.key === aspectKey;
              return (
                <button
                  key={a.key}
                  type="button"
                  onClick={() => handleAspectChange(a.key)}
                  data-testid={`editor-aspect-${a.key}`}
                  aria-pressed={active}
                  className={`shrink-0 rounded-full text-xs px-3 py-1.5 transition border ${
                    active
                      ? "bg-leaf text-white border-leaf"
                      : "bg-white/10 text-white/85 border-white/15 hover:bg-white/20"
                  }`}
                >
                  {t(a.labelKey)}
                </button>
              );
            })}
          </div>

          <div className="flex items-center justify-center gap-2.5">
            <button
              type="button"
              onClick={rotate}
              data-testid="editor-rotate"
              className="rounded-full bg-white/10 hover:bg-white/15 text-white px-4 py-2.5 text-sm flex items-center gap-1.5 transition"
            >
              <RotateCw className="w-4 h-4" strokeWidth={1.5} />
              {t("editor.rotate")}
            </button>
            {/* Reset box — recentres the crop rectangle to the current
                aspect at 90 % / 92 % width. Rescues users on the small
                set of Android browsers where the initial free seed can
                still get pushed off-screen by an edge-swipe gesture, and
                gives everyone a quick "start over" without touching the
                already-selected aspect or rotation. */}
            <button
              type="button"
              onClick={() => seedCropForAspect(imgRef.current, aspectRatio)}
              data-testid="editor-reset-box"
              aria-label={t("editor.resetBoxAria")}
              className="rounded-full bg-white/10 hover:bg-white/15 text-white px-4 py-2.5 text-sm flex items-center gap-1.5 transition"
            >
              <Frame className="w-4 h-4" strokeWidth={1.5} />
              {t("editor.resetBox")}
            </button>
            {(rotation !== 0 || aspectKey !== "free" || isDirty) && (
              <button
                type="button"
                onClick={reset}
                data-testid="editor-reset"
                aria-label={t("editor.undo")}
                className="rounded-full bg-white/10 hover:bg-white/15 text-white px-4 py-2.5 text-sm flex items-center gap-1.5 transition"
              >
                <Undo2 className="w-4 h-4" strokeWidth={1.5} />
                {t("editor.undo")}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
